<template>
  <div class="dashboard">
    <h2 class="dashboard-page-title">{{ __('Dashboard') }}</h2>
    <AlertBanner
      v-if="taskAlert"
      :type="taskAlert.type"
      :title="taskAlert.title"
      :message="taskAlert.message"
      :link-text="taskAlert.linkText"
      :link-url="taskAlert.linkUrl"
      @dismiss="taskAlert = null"
    />

    <div
      class="dashboard-layout"
      :class="{ 'is-sidebar-collapsed': isSidebarCollapsed && canCollapseSidebar }"
      :style="{ '--dashboard-sticky-top': `${stickyTopOffset}px` }"
    >
      <DashboardNav
        :active-panel="activePanel"
        :collapsed="isSidebarCollapsed && canCollapseSidebar"
        :can-collapse="canCollapseSidebar"
        :is-full-width="props.isFullWidth"
        :can-toggle-full-width="props.canToggleFullWidth"
        :boards-enabled="boardsEnabled"
        :s3-attachments-enabled="s3AttachmentsEnabled"
        :calendar-url="calendarUrl"
        :profile-url="profileUrl"
        @toggle-panel="togglePanel"
        @toggle-collapse="toggleSidebarCollapse"
        @toggle-full-width="emit('toggle-full-width')"
      />

      <div class="dashboard-content">
        <ProjectsExpandableBlock
          v-if="activePanel === 'projects'"
          :loading="projectsLoading && !loadingMoreProjects"
          :projects="projects"
          :loading-more-projects="loadingMoreProjects"
          :projects-has-next-page="projectsHasNextPage"
          :project-url-builder="projectUrl"
          @load-more="loadMoreProjects"
          @open-create-project="openProjectCreateModal"
          @open-create-task="openTaskCreateForProject"
        />

        <TasksExpandableBlock
          v-else-if="activePanel === 'tasks'"
          :loading="tasksLoading && !loadingMoreTasks"
          :tasks="tasks"
          :loading-more-tasks="loadingMoreTasks"
          :tasks-has-next-page="tasksHasNextPage"
          :filter-as-author="filterAsAuthor"
          :filter-as-assignee="filterAsAssignee"
          :filter-as-subscriber="filterAsSubscriber"
          :filter-all-accessible="filterAllAccessible"
          :advanced-filters="advancedFilters"
          :advanced-active-count="advancedActiveCount"
          :group-by="taskGrouping"
          :order-by="taskOrderBy"
          :order-direction="taskOrderDirection"
          :members="filterMembers"
          :priorities="priorityOptions"
          :workflow-statuses="filterWorkflowStatuses"
          :grouped-workflow-statuses="groupedWorkflowStatuses"
          :labels="filterLabels"
          :task-categories="filterTaskCategories"
          :grouped-task-categories="groupedTaskCategories"
          :projects-for-filter="filterProjects"
          :personal-labels-enabled="personalLabelsEnabled"
          :chat-id="chatId"
          :from-id="fromId"
          :user-timezone="userTimezone"
          :user-locale="userLocale"
          :has-stored-default-settings="hasStoredDefaultSettings"
          :saving-default-settings="savingDefaultTaskListSettings"
          :dashboard-presentation="dashboardPresentation"
          :saving-dashboard-presentation="savingDashboardPresentation"
          dashboard-scope="personal"
          @load-more="loadMoreTasks"
          @update-role-filters="applyRoleFilters"
          @apply-advanced-filters="applyAdvancedFilters"
          @update-group-by="updateTaskGrouping"
          @update-ordering="updateTaskOrdering"
          @reset-ordering="resetTaskOrdering"
          @transition-executed="handleTransitionExecuted"
          @open-create-task="openTaskCreateFromTasks"
          @save-as-default="saveCurrentTaskListSettingsAsDefault"
          @restore-saved-settings="restoreSavedTaskListSettings"
          @update-presentation="updateDashboardPresentation"
        />

        <ConstructorPanel
          v-else-if="activePanel === 'reports'"
          :project-id="null"
          :chat-id="chatId"
          :from-id="fromId"
        />

        <BoardsIndex
          v-else-if="activePanel === 'boards'"
          scope="user"
          :chat-id="chatId"
          :from-id="fromId"
          embedded
        />

        <UserAttachments
          v-else-if="activePanel === 'attachments'"
          :chat-id="chatId"
          :from-id="fromId"
          embedded
        />

        <div v-else class="dashboard-empty-message">
          {{
            s__('Dashboard|Select Projects, Tasks, Reports, Boards, or Files to expand the block.')
          }}
        </div>
      </div>
    </div>

    <DashboardTaskCreateModal
      :visible="isTaskCreateModalVisible"
      :projects="taskCreateProjects"
      :initial-project-id="taskCreateInitialProjectId"
      :chat-id="chatId"
      :from-id="fromId"
      @close="closeTaskCreateModal"
      @created="handleTaskCreated"
      @open-project-create="switchTaskCreateToProjectCreate"
    />

    <DashboardProjectCreateModal
      :visible="isProjectCreateModalVisible"
      :chat-id="chatId"
      :from-id="fromId"
      :projects="projects"
      :premium-active="premiumActive"
      :has-telegram-user-id="props.hasTelegramUserId"
      @close="closeProjectCreateModal"
      @created="handleProjectCreated"
    />
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useMutation, useQuery } from '@vue/apollo-composable'
import { gql } from '@apollo/client/core'
import { __, s__ } from '@/locale'
import { useCurrentUser } from '@/composables/useCurrentUser'
import AlertBanner from '../common/AlertBanner.vue'
import BoardsIndex from '../boards/Index.vue'
import UserAttachments from '../profile/UserAttachments.vue'
import DashboardNav from './DashboardNav.vue'
import DashboardProjectCreateModal from './DashboardProjectCreateModal.vue'
import DashboardTaskCreateModal from './DashboardTaskCreateModal.vue'
import ProjectsExpandableBlock from './ProjectsExpandableBlock.vue'
import TasksExpandableBlock from './TasksExpandableBlock.vue'
import ConstructorPanel from '../reports/ConstructorPanel.vue'
import {
  readSidebarCollapsedPreference,
  writeSidebarCollapsedPreference,
} from '@/utils/sidebarCollapsePreference'

const props = defineProps({
  chatId: { type: String, default: null },
  fromId: { type: String, default: null },
  hasTelegramUserId: { type: Boolean, default: false },
  isFullWidth: { type: Boolean, default: false },
  canToggleFullWidth: { type: Boolean, default: false },
})
const emit = defineEmits(['toggle-full-width'])

const DASHBOARD_QUICK_FILTERS_SESSION_KEY = 'dashboardQuickRoleFilters'
const DASHBOARD_TASK_SETTINGS_SESSION_KEY = 'dashboardTaskSettings'
const DASHBOARD_ACTIVE_PANEL_SESSION_KEY = 'dashboardActivePanel'

function hasSessionValue(key) {
  try {
    return sessionStorage.getItem(key) !== null
  } catch {
    return false
  }
}

function defaultAdvancedFilters() {
  return {
    search: '',
    authorIds: [],
    assigneeIds: [],
    priorities: [],
    workflowStatusIds: [],
    taskCategoryIds: [],
    labelIds: [],
    personalLabelIds: [],
    projectIds: [],
    dueFrom: '',
    dueTo: '',
    dueInDays: null,
    includeFinished: false,
    includeArchived: false,
    includeOverdue: true,
  }
}

function restoreQuickFiltersFromSession() {
  try {
    const stored = sessionStorage.getItem(DASHBOARD_QUICK_FILTERS_SESSION_KEY)
    if (!stored) {
      return {
        filterAsAuthor: true,
        filterAsAssignee: true,
        filterAsSubscriber: true,
        filterAllAccessible: false,
      }
    }
    const parsed = JSON.parse(stored)
    return {
      filterAsAuthor: parsed.filterAsAuthor !== undefined ? parsed.filterAsAuthor : true,
      filterAsAssignee: parsed.filterAsAssignee !== undefined ? parsed.filterAsAssignee : true,
      filterAsSubscriber:
        parsed.filterAsSubscriber !== undefined ? parsed.filterAsSubscriber : true,
      filterAllAccessible:
        parsed.filterAllAccessible !== undefined ? parsed.filterAllAccessible : false,
    }
  } catch {
    return {
      filterAsAuthor: true,
      filterAsAssignee: true,
      filterAsSubscriber: true,
      filterAllAccessible: false,
    }
  }
}

function restoreTaskSettingsFromSession() {
  const fallback = {
    advancedFilters: defaultAdvancedFilters(),
    taskGrouping: 'by_due_date',
    taskOrderBy: 'due_at',
    taskOrderDirection: 'asc',
  }
  try {
    const stored = sessionStorage.getItem(DASHBOARD_TASK_SETTINGS_SESSION_KEY)
    if (!stored) return fallback
    const parsed = JSON.parse(stored)
    return {
      advancedFilters: {
        ...defaultAdvancedFilters(),
        ...(parsed.advancedFilters || {}),
      },
      taskGrouping: parsed.taskGrouping || 'by_due_date',
      taskOrderBy: parsed.taskOrderBy || 'due_at',
      taskOrderDirection: parsed.taskOrderDirection || 'asc',
    }
  } catch {
    return fallback
  }
}

const hasQuickFiltersInSession = hasSessionValue(DASHBOARD_QUICK_FILTERS_SESSION_KEY)
const hasTaskSettingsInSession = hasSessionValue(DASHBOARD_TASK_SETTINGS_SESSION_KEY)
const restoredQuick = restoreQuickFiltersFromSession()
const restoredSettings = restoreTaskSettingsFromSession()

const filterAsAuthor = ref(restoredQuick.filterAsAuthor)
const filterAsAssignee = ref(restoredQuick.filterAsAssignee)
const filterAsSubscriber = ref(restoredQuick.filterAsSubscriber)
const filterAllAccessible = ref(restoredQuick.filterAllAccessible === true)
const advancedFilters = ref(restoredSettings.advancedFilters)
const taskGrouping = ref(restoredSettings.taskGrouping)
const taskOrderBy = ref(restoredSettings.taskOrderBy)
const taskOrderDirection = ref(restoredSettings.taskOrderDirection)

if (filterAllAccessible.value) {
  filterAsAuthor.value = true
  filterAsAssignee.value = true
  filterAsSubscriber.value = true
}

const activePanel = ref('projects')
const hasAutoOpenedProjectsPanel = ref(false)
const canCollapseSidebar = ref(false)
const isSidebarCollapsed = ref(false)
const stickyTopOffset = ref(12)
const projects = ref([])
const projectsHasNextPage = ref(false)
const projectsEndCursor = ref(null)
const loadingMoreProjects = ref(false)
const tasks = ref([])
const tasksHasNextPage = ref(false)
const tasksEndCursor = ref(null)
const loadingMoreTasks = ref(false)
const taskAlert = ref(null)
const isTaskCreateModalVisible = ref(false)
const isProjectCreateModalVisible = ref(false)
const taskCreateInitialProjectId = ref(null)
const taskCreateProjects = ref([])
const savedDefaultTaskList = ref(null)
const hasStoredDefaultSettings = ref(false)
const savingDefaultTaskListSettings = ref(false)
const storedDefaultSettingsApplied = ref(false)

const filterProjects = ref([])
const filterMembers = ref([])
const filterWorkflowStatuses = ref([])
const groupedWorkflowStatuses = ref({})
const filterLabels = ref([])
const filterTaskCategories = ref([])
const groupedTaskCategories = ref({})
const priorityOptions = ref([])
const personalLabelsEnabled = ref(false)
const dashboardPresentation = ref('list')
const savingDashboardPresentation = ref(false)

function restoreActivePanelFromUrl() {
  if (typeof window === 'undefined') return null
  const tab = new URLSearchParams(window.location.search).get('tab')
  return ['projects', 'tasks', 'reports', 'boards', 'attachments'].includes(tab) ? tab : null
}

function restoreActivePanelFromSession() {
  const panelFromUrl = restoreActivePanelFromUrl()
  if (panelFromUrl) return panelFromUrl
  try {
    const stored = sessionStorage.getItem(DASHBOARD_ACTIVE_PANEL_SESSION_KEY)
    return stored === 'tasks' ||
      stored === 'projects' ||
      stored === 'reports' ||
      stored === 'boards' ||
      stored === 'attachments'
      ? stored
      : 'tasks'
  } catch {
    return 'tasks'
  }
}

function restoreSidebarCollapsedFromSession() {
  return readSidebarCollapsedPreference()
}

activePanel.value = restoreActivePanelFromSession()
isSidebarCollapsed.value = restoreSidebarCollapsedFromSession()

function updateSidebarCapabilities() {
  if (typeof window === 'undefined') return
  canCollapseSidebar.value = window.innerWidth >= 1024
}

function updateStickyTopOffset() {
  if (typeof window === 'undefined') return
  const header = document.querySelector('.app-header')
  if (!(header instanceof HTMLElement)) {
    stickyTopOffset.value = 12
    return
  }
  const headerStyles = window.getComputedStyle(header)
  if (headerStyles.position !== 'sticky') {
    stickyTopOffset.value = 12
    return
  }
  const top = parseFloat(headerStyles.top || '0') || 0
  stickyTopOffset.value = Math.ceil(header.offsetHeight + top + 12)
}

function updateDashboardLayoutMetrics() {
  updateSidebarCapabilities()
  updateStickyTopOffset()
}

function toggleSidebarCollapse() {
  if (!canCollapseSidebar.value) return
  isSidebarCollapsed.value = !isSidebarCollapsed.value
}

const { userId, userTimezone, userLocale, boardsEnabled, premiumActive } = useCurrentUser({
  fromId: props.fromId,
  removeLoadingOnComplete: true,
})

const s3AttachmentsEnabled = computed(() => window.gon?.s3_attachments_enabled === true)

const calendarUrl = computed(() => buildBaseUrl('/calendar'))
const profileUrl = computed(() => buildBaseUrl('/profile'))
function buildBaseUrl(path) {
  let url = path
  const params = []
  if (props.chatId) params.push(`chat_id=${encodeURIComponent(props.chatId)}`)
  if (props.fromId) params.push(`from_id=${encodeURIComponent(props.fromId)}`)
  if (params.length > 0) url += '?' + params.join('&')
  return url
}

function projectUrl(project) {
  const projectSegment = project?.slug || project?.id
  return buildBaseUrl(`/projects/${encodeURIComponent(projectSegment)}/dashboard`)
}

const PROJECTS_Q = gql`
  query DashboardProjects($userId: ID!, $first: Int, $after: String) {
    user(id: $userId) {
      id
      projects(first: $first, after: $after) {
        edges {
          node {
            id
            slug
            title
            description
            isPersonal
            archived
            tasksCount
            membersCount
            userRole(userId: $userId)
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`

const FILTER_OPTIONS_Q = gql`
  query DashboardFilterOptions {
    currentUser {
      id
      telegramUserId
      personalLabelsEnabled
      preferredDashboardPresentation
      defaultTaskList {
        id
        title
        scopeDefault
        preFilters
        swimlaneMode
        orderBy
        orderDirection
        asAuthor
        asAssignee
        asSubscriber
        allAccessible
      }
      projects(first: 100) {
        edges {
          node {
            id
            title
            archived
            labels {
              id
              title
              color
            }
            members {
              id
              name
              username
            }
            taskCategories {
              id
              name
              workflow {
                id
                name
                statuses {
                  id
                  name
                  color
                  position
                }
              }
            }
          }
        }
      }
    }
    priorityOptions {
      value
      label
    }
  }
`

const SAVE_DEFAULT_TASK_LIST_MUTATION = gql`
  mutation SaveDefaultTaskList(
    $title: String
    $preFilters: JSON!
    $swimlaneMode: String!
    $orderBy: String!
    $orderDirection: String!
    $asAuthor: Boolean!
    $asAssignee: Boolean!
    $asSubscriber: Boolean!
    $allAccessible: Boolean!
  ) {
    saveDefaultTaskList(
      title: $title
      preFilters: $preFilters
      swimlaneMode: $swimlaneMode
      orderBy: $orderBy
      orderDirection: $orderDirection
      asAuthor: $asAuthor
      asAssignee: $asAssignee
      asSubscriber: $asSubscriber
      allAccessible: $allAccessible
    ) {
      taskList {
        id
        title
        scopeDefault
        preFilters
        swimlaneMode
        orderBy
        orderDirection
        asAuthor
        asAssignee
        asSubscriber
        allAccessible
      }
      errors
    }
  }
`

const UPDATE_DASHBOARD_PRESENTATION_MUTATION = gql`
  mutation UpdateDashboardPresentation($userId: ID!, $preferredDashboardPresentation: String) {
    updateUserPreferences(
      userId: $userId
      preferredDashboardPresentation: $preferredDashboardPresentation
    ) {
      user {
        id
        preferredDashboardPresentation
      }
      errors
    }
  }
`

const TASKS_Q = gql`
  query DashboardTasks(
    $userId: ID!
    $first: Int
    $after: String
    $projectIds: [ID!]
    $priorities: [String!]
    $workflowStatusIds: [ID!]
    $taskCategoryIds: [ID!]
    $labelIds: [ID!]
    $personalLabelIds: [ID!]
    $assigneeIds: [ID!]
    $authorIds: [ID!]
    $dueFrom: ISO8601DateTime
    $dueTo: ISO8601DateTime
    $dueInDays: Int
    $includeOverdue: Boolean
    $includeArchived: Boolean
    $search: String
    $orderBy: String
    $orderDirection: String
    $asAuthor: Boolean
    $asAssignee: Boolean
    $asSubscriber: Boolean
    $excludeFinal: Boolean
  ) {
    user(id: $userId) {
      id
      tasks(
        first: $first
        after: $after
        projectIds: $projectIds
        priorities: $priorities
        workflowStatusIds: $workflowStatusIds
        taskCategoryIds: $taskCategoryIds
        labelIds: $labelIds
        personalLabelIds: $personalLabelIds
        assigneeIds: $assigneeIds
        authorIds: $authorIds
        dueFrom: $dueFrom
        dueTo: $dueTo
        dueInDays: $dueInDays
        includeOverdue: $includeOverdue
        includeArchived: $includeArchived
        search: $search
        orderBy: $orderBy
        orderDirection: $orderDirection
        asAuthor: $asAuthor
        asAssignee: $asAssignee
        asSubscriber: $asSubscriber
        excludeFinal: $excludeFinal
      ) {
        edges {
          node {
            id
            iid
            title
            priority
            archived
            dueAt
            finishedAt
            createdAt
            updatedAt
            workflowStatus {
              id
              name
              color
              isFinal
            }
            taskCategory {
              id
              name
            }
            labels {
              id
              title
              color
            }
            personalLabels {
              id
              title
              color
            }
            author {
              id
              username
              name
            }
            assignee {
              id
              username
              name
            }
            project {
              id
              slug
              title
            }
          }
          cursor
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`

function cleanVariables(raw) {
  return Object.entries(raw).reduce((acc, [k, v]) => {
    if (v === undefined || v === null) return acc
    if (typeof v === 'string' && v.trim() === '') return acc
    if (Array.isArray(v) && v.length === 0) return acc
    acc[k] = v
    return acc
  }, {})
}

function buildTasksVariables(after = null) {
  const filters = advancedFilters.value || defaultAdvancedFilters()
  const search = filters.search && filters.search.trim()
  const validSearch = search && search.length >= 3 ? search : undefined
  const showAllAccessibleTasks = filterAllAccessible.value === true

  return cleanVariables({
    userId: userId.value,
    first: 25,
    after,
    projectIds: filters.projectIds,
    priorities: filters.priorities,
    workflowStatusIds: filters.workflowStatusIds,
    taskCategoryIds: filters.taskCategoryIds,
    labelIds: filters.labelIds,
    personalLabelIds: filters.personalLabelIds,
    assigneeIds: filters.assigneeIds,
    authorIds: filters.authorIds,
    dueFrom: filters.dueFrom || undefined,
    dueTo: filters.dueTo || undefined,
    dueInDays: filters.dueInDays ? parseInt(filters.dueInDays, 10) : undefined,
    includeOverdue: filters.includeOverdue !== undefined ? filters.includeOverdue : undefined,
    includeArchived: filters.includeArchived || undefined,
    search: validSearch,
    orderBy: taskOrderBy.value || undefined,
    orderDirection: taskOrderDirection.value || undefined,
    asAuthor: showAllAccessibleTasks ? undefined : filterAsAuthor.value,
    asAssignee: showAllAccessibleTasks ? undefined : filterAsAssignee.value,
    asSubscriber: showAllAccessibleTasks ? undefined : filterAsSubscriber.value,
    excludeFinal: filters.includeFinished ? undefined : true,
  })
}

const {
  result: projectsResult,
  loading: projectsLoading,
  fetchMore: fetchMoreProjects,
  refetch: refetchProjects,
} = useQuery(
  PROJECTS_Q,
  () => ({ userId: userId.value, first: 25, after: null }),
  () => ({ enabled: !!userId.value })
)

const { result: filterOptionsResult } = useQuery(
  FILTER_OPTIONS_Q,
  () => ({}),
  () => ({
    enabled: !!userId.value && (activePanel.value === 'tasks' || isTaskCreateModalVisible.value),
  })
)

const {
  result: tasksResult,
  loading: tasksLoading,
  fetchMore: fetchMoreTasks,
  refetch: refetchTasks,
} = useQuery(
  TASKS_Q,
  () => buildTasksVariables(null),
  () => ({ enabled: !!userId.value && activePanel.value === 'tasks' })
)

const { mutate: saveDefaultTaskList } = useMutation(SAVE_DEFAULT_TASK_LIST_MUTATION)
const { mutate: saveDashboardPresentation } = useMutation(UPDATE_DASHBOARD_PRESENTATION_MUTATION)

const advancedActiveCount = computed(() => {
  let count = 0
  const filters = advancedFilters.value
  if (filters.search && filters.search.trim().length >= 3) count++
  if (filters.priorities?.length) count++
  if (filters.workflowStatusIds?.length) count++
  if (filters.taskCategoryIds?.length) count++
  if (filters.labelIds?.length) count++
  if (filters.personalLabelIds?.length) count++
  if (filters.authorIds?.length) count++
  if (filters.assigneeIds?.length) count++
  if (filters.projectIds?.length) count++
  if (filters.dueFrom) count++
  if (filters.dueTo) count++
  if (filters.dueInDays) count++
  if (filters.includeFinished) count++
  if (filters.includeArchived) count++
  if (filters.includeOverdue === false) count++
  if (taskGrouping.value !== 'by_due_date') count++
  if (taskOrderBy.value !== 'due_at' || taskOrderDirection.value !== 'asc') count++
  return count
})

function clearTaskListSettingsSessionState() {
  try {
    sessionStorage.removeItem(DASHBOARD_QUICK_FILTERS_SESSION_KEY)
    sessionStorage.removeItem(DASHBOARD_TASK_SETTINGS_SESSION_KEY)
  } catch {}
}

function normalizeStoredListFilters(preFilters) {
  return {
    ...defaultAdvancedFilters(),
    ...(preFilters || {}),
  }
}

function applyStoredTaskListSettings(taskList) {
  if (!taskList) return

  filterAsAuthor.value = taskList.asAuthor !== false
  filterAsAssignee.value = taskList.asAssignee !== false
  filterAsSubscriber.value = taskList.asSubscriber !== false
  filterAllAccessible.value = taskList.allAccessible === true
  if (filterAllAccessible.value) {
    filterAsAuthor.value = true
    filterAsAssignee.value = true
    filterAsSubscriber.value = true
  }

  advancedFilters.value = normalizeStoredListFilters(taskList.preFilters)
  taskGrouping.value = taskList.swimlaneMode || 'by_due_date'
  taskOrderBy.value = taskList.orderBy || 'due_at'
  taskOrderDirection.value = taskList.orderDirection || 'asc'
}

watch(
  projectsResult,
  data => {
    if (loadingMoreProjects.value) return
    const user = data?.user
    if (!user) {
      projects.value = []
      projectsHasNextPage.value = false
      projectsEndCursor.value = null
      return
    }
    const allProjects = user.projects?.edges ? user.projects.edges.map(edge => edge.node) : []
    projects.value = [...allProjects].sort((a, b) => {
      const aPersonal = a.isPersonal === true
      const bPersonal = b.isPersonal === true
      if (aPersonal && !bPersonal) return -1
      if (!aPersonal && bPersonal) return 1
      return 0
    })

    if (
      !hasAutoOpenedProjectsPanel.value &&
      activePanel.value !== 'projects' &&
      allProjects.length === 0
    ) {
      activePanel.value = 'projects'
    }
    hasAutoOpenedProjectsPanel.value = true

    projectsHasNextPage.value = user.projects?.pageInfo?.hasNextPage || false
    projectsEndCursor.value = user.projects?.pageInfo?.endCursor || null
  },
  { immediate: true }
)

watch(
  tasksResult,
  data => {
    if (loadingMoreTasks.value) return
    const user = data?.user
    if (user?.tasks?.edges) {
      tasks.value = user.tasks.edges.map(edge => edge.node)
      tasksHasNextPage.value = user.tasks.pageInfo?.hasNextPage || false
      tasksEndCursor.value = user.tasks.pageInfo?.endCursor || null
      return
    }
    tasks.value = []
    tasksHasNextPage.value = false
    tasksEndCursor.value = null
  },
  { immediate: true }
)

watch(
  filterOptionsResult,
  data => {
    priorityOptions.value = data?.priorityOptions || []
    const user = data?.currentUser
    personalLabelsEnabled.value = user?.personalLabelsEnabled === true
    dashboardPresentation.value = normalizeDashboardPresentation(
      user?.preferredDashboardPresentation
    )
    savedDefaultTaskList.value = user?.defaultTaskList || null
    hasStoredDefaultSettings.value = !!savedDefaultTaskList.value

    const edges = user?.projects?.edges || []
    const projectsForFilter = []
    const membersById = new Map()
    const statuses = []
    const labels = []
    const categories = []
    const groupedStatuses = {}
    const groupedCategories = {}

    edges.forEach(edge => {
      const project = edge.node
      if (!project || project.archived) return

      projectsForFilter.push({ id: project.id, title: project.title })
      ;(project.members || []).forEach(member => {
        if (!member || member.id == null) return
        const key = String(member.id)
        if (!membersById.has(key)) {
          membersById.set(key, member)
        }
      })
      ;(project.labels || []).forEach(label => {
        if (!labels.find(l => String(l.id) === String(label.id))) labels.push(label)
      })

      groupedCategories[project.title] = (project.taskCategories || []).map(taskCategory => ({
        id: taskCategory.id,
        name: taskCategory.name,
      }))
      ;(project.taskCategories || []).forEach(taskCategory => {
        if (!categories.find(c => String(c.id) === String(taskCategory.id))) {
          categories.push({ id: taskCategory.id, name: taskCategory.name })
        }

        if (taskCategory.workflow?.statuses) {
          const key = `${project.id}_${taskCategory.workflow.id}`
          if (!groupedStatuses[key]) {
            groupedStatuses[key] = {
              project: { id: project.id, title: project.title },
              workflow: { id: taskCategory.workflow.id, name: taskCategory.workflow.name },
              taskCategory: { id: taskCategory.id, name: taskCategory.name },
              statuses: [],
            }
          }

          taskCategory.workflow.statuses.forEach(status => {
            if (!groupedStatuses[key].statuses.find(s => String(s.id) === String(status.id))) {
              groupedStatuses[key].statuses.push(status)
            }
            if (!statuses.find(s => String(s.id) === String(status.id))) {
              statuses.push(status)
            }
          })
        }
      })
    })

    Object.keys(groupedStatuses).forEach(key => {
      groupedStatuses[key].statuses.sort((a, b) => a.position - b.position)
    })

    const sortedMembers = Array.from(membersById.values()).sort((a, b) => {
      const aTitle = (a.username || a.name || '').toLowerCase()
      const bTitle = (b.username || b.name || '').toLowerCase()
      return aTitle.localeCompare(bTitle)
    })

    filterProjects.value = projectsForFilter.sort((a, b) => a.title.localeCompare(b.title))
    filterMembers.value = sortedMembers
    filterWorkflowStatuses.value = statuses.sort((a, b) => a.position - b.position)
    groupedWorkflowStatuses.value = groupedStatuses
    filterLabels.value = labels
    filterTaskCategories.value = categories
    groupedTaskCategories.value = groupedCategories

    if (
      !storedDefaultSettingsApplied.value &&
      !hasQuickFiltersInSession &&
      !hasTaskSettingsInSession &&
      savedDefaultTaskList.value
    ) {
      applyStoredTaskListSettings(savedDefaultTaskList.value)
      storedDefaultSettingsApplied.value = true
    }
  },
  { immediate: true }
)

watch(
  [filterAsAuthor, filterAsAssignee, filterAsSubscriber, filterAllAccessible],
  () => {
    tasksEndCursor.value = null
    try {
      sessionStorage.setItem(
        DASHBOARD_QUICK_FILTERS_SESSION_KEY,
        JSON.stringify({
          filterAsAuthor: filterAsAuthor.value,
          filterAsAssignee: filterAsAssignee.value,
          filterAsSubscriber: filterAsSubscriber.value,
          filterAllAccessible: filterAllAccessible.value,
        })
      )
    } catch {}
  },
  { immediate: false }
)

watch(
  [advancedFilters, taskGrouping, taskOrderBy, taskOrderDirection],
  () => {
    try {
      sessionStorage.setItem(
        DASHBOARD_TASK_SETTINGS_SESSION_KEY,
        JSON.stringify({
          advancedFilters: advancedFilters.value,
          taskGrouping: taskGrouping.value,
          taskOrderBy: taskOrderBy.value,
          taskOrderDirection: taskOrderDirection.value,
        })
      )
    } catch {}
  },
  { deep: true }
)

watch(
  [
    filterAsAuthor,
    filterAsAssignee,
    filterAsSubscriber,
    filterAllAccessible,
    advancedFilters,
    taskOrderBy,
    taskOrderDirection,
  ],
  () => {
    tasksEndCursor.value = null
    if (activePanel.value === 'tasks') {
      refetchTasks(buildTasksVariables(null))
    }
  },
  { deep: true }
)

watch(
  activePanel,
  panel => {
    if (
      panel !== 'projects' &&
      panel !== 'tasks' &&
      panel !== 'reports' &&
      panel !== 'boards' &&
      panel !== 'attachments'
    )
      return
    try {
      sessionStorage.setItem(DASHBOARD_ACTIVE_PANEL_SESSION_KEY, panel)
    } catch {}
  },
  { immediate: true }
)

watch(
  isSidebarCollapsed,
  value => {
    writeSidebarCollapsedPreference(value)
  },
  { immediate: true }
)

function togglePanel(panelName) {
  activePanel.value = activePanel.value === panelName ? null : panelName
}

function openTaskCreateForProject(project) {
  taskCreateProjects.value = [{ id: project?.id, title: project?.title }]
  taskCreateInitialProjectId.value = project?.id || null
  isTaskCreateModalVisible.value = true
}

function openTaskCreateFromTasks() {
  const availableProjects = filterProjects.value?.length
    ? filterProjects.value
    : projects.value.map(project => ({ id: project.id, title: project.title }))
  taskCreateProjects.value = [...availableProjects]
  taskCreateInitialProjectId.value = null
  isTaskCreateModalVisible.value = true
}

function openProjectCreateModal() {
  isProjectCreateModalVisible.value = true
}

function closeTaskCreateModal() {
  isTaskCreateModalVisible.value = false
  taskCreateInitialProjectId.value = null
  taskCreateProjects.value = []
}

function closeProjectCreateModal() {
  isProjectCreateModalVisible.value = false
}

function switchTaskCreateToProjectCreate() {
  closeTaskCreateModal()
  openProjectCreateModal()
}

function incrementProjectTasksCount(projectId) {
  if (!projectId) return

  const projectIdStr = String(projectId)
  projects.value = projects.value.map(project => {
    if (String(project.id) !== projectIdStr) return project

    const currentCount = Number(project.tasksCount)
    const safeCount = Number.isFinite(currentCount) ? currentCount : 0
    return {
      ...project,
      tasksCount: safeCount + 1,
    }
  })
}

function handleTaskCreated(payload) {
  incrementProjectTasksCount(payload?.projectId)
  closeTaskCreateModal()
  const taskUrl = buildTaskUrl(payload)
  taskAlert.value = {
    type: 'success',
    title: s__('TaskForm|Task created.'),
    message: payload?.title
      ? createdTaskMessage(payload.title, taskUrl)
      : s__('Dashboard|Task has been created successfully.'),
    linkText: taskUrl ? payload.title : '',
    linkUrl: taskUrl,
  }
  if (activePanel.value === 'tasks') {
    refetchTasks(buildTasksVariables(null))
  }
  if (activePanel.value === 'projects' && payload?.projectId) {
    refetchProjects({ userId: userId.value, first: 25, after: null })
  }
}

function createdTaskMessage(title, taskUrl) {
  const message = s__('Dashboard|Created task: %{title}')

  return taskUrl ? message.replace('%{title}', '').trim() : message.replace('%{title}', title)
}

function buildTaskUrl(payload) {
  if (!payload?.projectId || !payload?.taskIid) return ''

  const url = new URL(
    `/projects/${encodeURIComponent(payload.projectId)}/tasks/${encodeURIComponent(payload.taskIid)}`,
    window.location.origin
  )

  if (payload.chatId) url.searchParams.set('chat_id', payload.chatId)
  if (payload.fromId) url.searchParams.set('from_id', payload.fromId)

  return `${url.pathname}${url.search}`
}

function handleProjectCreated() {
  closeProjectCreateModal()
  if (userId.value) {
    refetchProjects({ userId: userId.value, first: 25, after: null })
  }
  taskAlert.value = {
    type: 'success',
    title: __('Success'),
    message: s__('Dashboard|Project has been created successfully.'),
  }
}

function showTaskListSettingsUpdatedAlert() {
  taskAlert.value = {
    type: 'info',
    title: __('Success'),
    message: s__('Dashboard|Task list settings updated for current session.'),
  }
}

function applyRoleFilters(filters) {
  filterAsAuthor.value = filters.filterAsAuthor
  filterAsAssignee.value = filters.filterAsAssignee
  filterAsSubscriber.value = filters.filterAsSubscriber
  filterAllAccessible.value = filters.filterAllAccessible === true
  showTaskListSettingsUpdatedAlert()
}

function applyAdvancedFilters(filters) {
  advancedFilters.value = {
    ...defaultAdvancedFilters(),
    ...filters,
  }
  showTaskListSettingsUpdatedAlert()
}

function updateTaskGrouping(groupBy) {
  taskGrouping.value = groupBy || 'by_due_date'
  showTaskListSettingsUpdatedAlert()
}

function updateTaskOrdering({ orderBy, orderDirection }) {
  taskOrderBy.value = orderBy || 'due_at'
  taskOrderDirection.value = orderDirection || 'asc'
  showTaskListSettingsUpdatedAlert()
}

function resetTaskOrdering() {
  taskOrderBy.value = 'due_at'
  taskOrderDirection.value = 'asc'
  showTaskListSettingsUpdatedAlert()
}

function normalizeDashboardPresentation(value) {
  return value === 'table' ? 'table' : 'list'
}

async function updateDashboardPresentation(value) {
  const normalizedValue = normalizeDashboardPresentation(value)
  if (
    !userId.value ||
    normalizedValue === dashboardPresentation.value ||
    savingDashboardPresentation.value
  )
    return

  const previousValue = dashboardPresentation.value
  dashboardPresentation.value = normalizedValue
  savingDashboardPresentation.value = true

  try {
    const response = await saveDashboardPresentation({
      userId: userId.value,
      preferredDashboardPresentation: normalizedValue,
    })
    const payload = response?.data?.updateUserPreferences
    if (payload?.errors?.length) {
      throw new Error(payload.errors.join(', '))
    }

    dashboardPresentation.value = normalizeDashboardPresentation(
      payload?.user?.preferredDashboardPresentation
    )
  } catch (error) {
    dashboardPresentation.value = previousValue
    taskAlert.value = {
      type: 'error',
      title: __('Error'),
      message: error.message || s__('Dashboard|Failed to update dashboard presentation.'),
    }
  } finally {
    savingDashboardPresentation.value = false
  }
}

async function saveCurrentTaskListSettingsAsDefault() {
  if (savingDefaultTaskListSettings.value) return

  savingDefaultTaskListSettings.value = true
  try {
    const result = await saveDefaultTaskList({
      title: savedDefaultTaskList.value?.title || 'Default',
      preFilters: advancedFilters.value || {},
      swimlaneMode: taskGrouping.value || 'by_due_date',
      orderBy: taskOrderBy.value || 'due_at',
      orderDirection: taskOrderDirection.value || 'asc',
      asAuthor: filterAsAuthor.value,
      asAssignee: filterAsAssignee.value,
      asSubscriber: filterAsSubscriber.value,
      allAccessible: filterAllAccessible.value,
    })
    const payload = result?.data?.saveDefaultTaskList
    if (payload?.errors?.length) {
      taskAlert.value = {
        type: 'error',
        title: __('Error'),
        message: payload.errors.join(', '),
      }
      return
    }

    savedDefaultTaskList.value = payload?.taskList || null
    hasStoredDefaultSettings.value = !!savedDefaultTaskList.value
    storedDefaultSettingsApplied.value = true
    taskAlert.value = {
      type: 'success',
      title: __('Success'),
      message: s__('Dashboard|Default task list settings saved.'),
    }
  } catch {
    taskAlert.value = {
      type: 'error',
      title: __('Error'),
      message: s__('Dashboard|Failed to save default task list settings.'),
    }
  } finally {
    savingDefaultTaskListSettings.value = false
  }
}

function restoreSavedTaskListSettings() {
  clearTaskListSettingsSessionState()
  if (!savedDefaultTaskList.value) {
    taskAlert.value = {
      type: 'info',
      title: __('Info'),
      message: s__('Dashboard|No saved default task list settings found.'),
    }
    return
  }

  applyStoredTaskListSettings(savedDefaultTaskList.value)
  storedDefaultSettingsApplied.value = true
  taskAlert.value = {
    type: 'success',
    title: __('Success'),
    message: s__('Dashboard|Saved task list settings restored.'),
  }
}

function handleTransitionExecuted() {
  if (activePanel.value === 'tasks') {
    refetchTasks(buildTasksVariables(null))
  }
}

async function loadMoreProjects() {
  if (loadingMoreProjects.value || !projectsHasNextPage.value || !projectsEndCursor.value) return
  loadingMoreProjects.value = true
  try {
    await fetchMoreProjects({
      variables: {
        userId: userId.value,
        first: 25,
        after: projectsEndCursor.value,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult?.user?.projects) return previousResult
        const previousEdges = previousResult.user.projects.edges || []
        const nextEdges = fetchMoreResult.user.projects.edges || []
        const updated = {
          ...previousResult,
          user: {
            ...previousResult.user,
            projects: {
              ...fetchMoreResult.user.projects,
              edges: [...previousEdges, ...nextEdges],
            },
          },
        }

        const mergedProjects = updated.user.projects.edges.map(edge => edge.node)
        projects.value = [...mergedProjects].sort((a, b) => {
          const aPersonal = a.isPersonal === true
          const bPersonal = b.isPersonal === true
          if (aPersonal && !bPersonal) return -1
          if (!aPersonal && bPersonal) return 1
          return 0
        })
        projectsHasNextPage.value = updated.user.projects.pageInfo?.hasNextPage || false
        projectsEndCursor.value = updated.user.projects.pageInfo?.endCursor || null
        return updated
      },
    })
  } finally {
    loadingMoreProjects.value = false
  }
}

async function loadMoreTasks() {
  if (loadingMoreTasks.value || !tasksHasNextPage.value || !tasksEndCursor.value) return
  loadingMoreTasks.value = true
  try {
    await fetchMoreTasks({
      variables: buildTasksVariables(tasksEndCursor.value),
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult?.user?.tasks) return previousResult
        const previousEdges = previousResult.user.tasks.edges || []
        const nextEdges = fetchMoreResult.user.tasks.edges || []
        const updated = {
          ...previousResult,
          user: {
            ...previousResult.user,
            tasks: {
              ...fetchMoreResult.user.tasks,
              edges: [...previousEdges, ...nextEdges],
            },
          },
        }
        tasks.value = updated.user.tasks.edges.map(edge => edge.node)
        tasksHasNextPage.value = updated.user.tasks.pageInfo?.hasNextPage || false
        tasksEndCursor.value = updated.user.tasks.pageInfo?.endCursor || null
        return updated
      },
    })
  } finally {
    loadingMoreTasks.value = false
  }
}

onMounted(() => {
  if (typeof window !== 'undefined') {
    updateDashboardLayoutMetrics()
    window.addEventListener('resize', updateDashboardLayoutMetrics)
  }
})

onBeforeUnmount(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', updateDashboardLayoutMetrics)
  }
})
</script>

<style src="../../styles/dashboard.css"></style>
